import { Button, Spinner } from '@heroui/react'
import type { BulkInvitePreview, ParsedBulkInviteError } from '@qse/api'
import { Alert } from '@qse/ui'
import { formatStorageValue } from '@qse/utils'
import { CheckCircle2, Info } from 'lucide-react'
import { useState } from 'react'

import BreakdownTable, { type BulkInviteOutcomeFilter } from './breakdown-table'
import StatusFilterChips from './status-filter-chips'

interface PreviewPanelProps {
  preview: BulkInvitePreview
  fileName?: string
  isSending: boolean
  /** A failure from Send — quota moved under us, or the re-upload target went active. */
  error: ParsedBulkInviteError | null
  /** Rows the admin has removed on this screen — a subset of the valid rows. */
  deletedRows: Set<number>
  /** Toggle a valid row's removed state. */
  onToggleDelete: (row: number) => void
  onBack: () => void
  onSend: () => void
}

/**
 * The Review step — what will happen when you press Send.
 *
 * Under the non-blocking model a bad row is not a blocker, so this screen's job is
 * to be honest rather than to gate: it states how many rows will be invited, and
 * lists the ones that won't be (and why) without preventing the send. Send stays
 * enabled unless there is genuinely nothing to send.
 */
export default function PreviewPanel({
  preview,
  fileName,
  isSending,
  error,
  deletedRows,
  onToggleDelete,
  onBack,
  onSend,
}: PreviewPanelProps) {
  const {
    validCount,
    totalRows,
    skippedEmptyCount,
    validRows,
    invalidRows,
    alreadyInvited,
    alreadyDoneCount,
    totals,
  } = preview

  // Which outcome the breakdown table shows. Local to this mount: re-validating
  // re-enters the step and deliberately starts back at 'all'.
  const [outcomeFilter, setOutcomeFilter] = useState<BulkInviteOutcomeFilter>('all')

  // Deletions are client-side until Send: only valid rows are removable, so a
  // deleted row moves one out of "will invite" and into "skipped". Counting
  // against validRows (not the raw set size) stays correct even if a stale row
  // number lingers in the set.
  const deletedValidCount = validRows.filter(row => deletedRows.has(row.row)).length
  const willInviteCount = validCount - deletedValidCount
  const skippedCount = invalidRows.length + alreadyInvited.length + deletedValidCount
  const nothingToSend = willInviteCount === 0

  return (
    <div className="flex flex-col gap-5 py-5 w-full">
      <div
        className={`flex items-center gap-3 rounded-lg border p-4 ${
          nothingToSend
            ? 'border-amber-500/30 bg-amber-500/10'
            : 'border-green-500/30 bg-green-500/10'
        }`}
      >
        {nothingToSend ? (
          <Info className="w-6 h-6 text-amber-500 shrink-0" />
        ) : (
          <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400 shrink-0" />
        )}
        <div>
          <p className="font-medium">
            {nothingToSend
              ? 'No rows left to invite'
              : `${willInviteCount.toLocaleString()} of ${totalRows.toLocaleString()} ${
                  totalRows === 1 ? 'row' : 'rows'
                } will be invited`}
          </p>
          {fileName && (
            <p className="text-xs text-gray-600 dark:text-[#AFAFAF] break-all">{fileName}</p>
          )}
        </div>
      </div>

      {error && (
        <Alert
          color="danger"
          description="Nothing was sent. Go back to re-check the columns, or try again."
          title={error.message}
        />
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Will invite" value={willInviteCount} />
        <Stat label="Skipped" value={skippedCount} />
        <Stat label="Total storage" text={formatStorageValue(totals.storageBytes)} />
        <Stat label="Total API quota" value={totals.apiCalls} />
      </div>

      {validRows.length + invalidRows.length + alreadyInvited.length > 0 && (
        <StatusFilterChips
          active={outcomeFilter}
          ariaLabel="Filter rows by outcome"
          options={[
            {
              key: 'all',
              label: 'All',
              count: validRows.length + invalidRows.length + alreadyInvited.length,
            },
            { key: 'valid', label: 'Valid', count: validRows.length },
            { key: 'invalid', label: 'Invalid', count: invalidRows.length },
            { key: 'already', label: 'Already invited', count: alreadyInvited.length },
          ]}
          onChange={key => setOutcomeFilter(key as BulkInviteOutcomeFilter)}
        />
      )}

      <BreakdownTable
        alreadyInvited={alreadyInvited}
        deletedRows={deletedRows}
        filter={outcomeFilter}
        invalidRows={invalidRows}
        validRows={validRows}
        onToggleDelete={onToggleDelete}
      />

      <div className="flex flex-col gap-1 text-xs text-gray-500 dark:text-[#AFAFAF]">
        {skippedEmptyCount > 0 && (
          <p>
            {skippedEmptyCount.toLocaleString()} blank{' '}
            {skippedEmptyCount === 1 ? 'row was' : 'rows were'} ignored.
          </p>
        )}
        {alreadyDoneCount > 0 && (
          <p>
            {alreadyDoneCount.toLocaleString()} {alreadyDoneCount === 1 ? 'row was' : 'rows were'}{' '}
            already invited by this request and won’t be sent again.
          </p>
        )}
        <p>
          {nothingToSend
            ? 'Nothing has been sent. Fix the rows above, or go back and re-check the columns.'
            : 'Nothing has been sent yet. Skipped rows are left alone — the rest are queued for sending.'}
        </p>
      </div>

      {isSending && (
        <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/10 p-3">
          <Spinner size="sm" />
          <p className="text-sm text-gray-700 dark:text-gray-200">
            Uploading and queuing your invitations… Please keep this dialog open until it finishes.
          </p>
        </div>
      )}

      <div className="flex justify-end gap-3 w-full">
        <Button isDisabled={isSending} radius="sm" variant="flat" onPress={onBack}>
          Back
        </Button>
        <Button
          color="primary"
          isDisabled={nothingToSend}
          isLoading={isSending}
          radius="sm"
          onPress={onSend}
        >
          Send invitations
        </Button>
      </div>
    </div>
  )
}

const Stat = ({ label, value, text }: { label: string; value?: number; text?: string }) => (
  <div className="rounded-lg border border-gray-200 dark:border-[#2A2A2A] p-3">
    <p className="text-xs text-gray-500 dark:text-[#AFAFAF]">{label}</p>
    <p className="text-lg font-medium">{text ?? (value ?? 0).toLocaleString()}</p>
  </div>
)
